import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Package, TrendingUp } from 'lucide-react';

function AnimatedCounter({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = Date.now();
          const tick = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
}

export default function SocialProofBar() {
  const { t } = useLanguage();

  return (
    <section className="relative bg-brand-brown py-5 overflow-hidden" data-testid="social-proof-bar">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1' fill='white'/%3E%3C/svg%3E")`,
        backgroundSize: '20px 20px'
      }} />
      
      <div className="section-container relative">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          {/* Box icon */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div className="text-white">
              <p className="text-2xl sm:text-3xl font-heading font-bold leading-none">
                <AnimatedCounter target={5100} />+
              </p>
            </div>
          </div>
          
          {/* Text */}
          <div className="flex items-center gap-2 text-white/90">
            <span className="text-sm sm:text-base font-medium">
              {t('social_proof_boxes')}
            </span>
            <TrendingUp className="w-4 h-4 text-brand-orange animate-pulse" />
            <span className="text-sm sm:text-base font-bold text-brand-orange">
              {t('social_proof_counting')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
